/**
 * SupplierManagement Component
 * 협력사 상세 페이지
 *
 * 협력사 기본 정보 관리
 * 참고: 협력사 수수료는 별도 메뉴(partner_fee)로, 프로모션은 정산 > 프로모션으로 분리됨
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { getItem } from '@/shared/utils/storage';
import { ArrowLeftIcon } from '@/shared/components/icons/Icons';
import SupplierDetailPage from './SupplierDetailPage';
import SupplierSelectionPage from './SupplierSelectionPage';
import Loading from '@/shared/components/feedback/Loading';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import '../styles/SupplierManagement.css';
import '@/shared/styles/common-table.css';

const SupplierManagement: React.FC = () => {
    // ========================================
    // 1. State - 로컬 상태 관리
    // ========================================
    const [userType, setUserType] = useState<string>('');
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
    const [selectedSupplierName, setSelectedSupplierName] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // 페이지 이동 확인 모달 관련 상태
    const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
    const hasUnsavedChangesRef = useRef<boolean>(false);

    // ========================================
    // 2. Side Effects - 초기 데이터 로드
    // ========================================
    /**
     * 사용자 타입 확인 및 협력사 ID 초기화
     * 협력사 사용자는 자동으로 자신의 ID 설정
     */
    useEffect(() => {
        const userTypeValue = getItem<string>('USER_TYPE');
        const userId = getItem<string>('USER_ID');
        setUserType(userTypeValue || '');

        if (userTypeValue === 'SUPPLIER' && userId) {
            setSelectedSupplierId(userId);
        }

        setIsInitialized(true);
    }, []);

    // ========================================
    // 3. Event Handlers
    // ========================================
    /**
     * 협력사 선택 (본사 사용자용)
     */
    const handleSupplierSelect = useCallback((supplierId: string, supplierName: string) => {
        setSelectedSupplierId(supplierId);
        setSelectedSupplierName(supplierName);
    }, []);

    /**
     * 협력사 목록으로 돌아가기 (본사 사용자용)
     */
    const handleBackToSelection = useCallback(() => {
        if (hasUnsavedChangesRef.current) {
            setShowLeaveModal(true);
        } else {
            setSelectedSupplierId('');
        }
    }, []);

    /**
     * 페이지 이동 확인 (저장하지 않고 이동)
     */
    const handleConfirmLeave = useCallback(() => {
        hasUnsavedChangesRef.current = false;
        setShowLeaveModal(false);
        setSelectedSupplierId('');
    }, []);

    /**
     * 페이지 이동 취소
     */
    const handleCancelLeave = useCallback(() => {
        setShowLeaveModal(false);
    }, []);

    /**
     * 변경사항 상태 업데이트 (자식 컴포넌트에서 호출)
     */
    const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
        hasUnsavedChangesRef.current = hasChanges;
    }, []);

    // ========================================
    // 4. Render - 초기화 대기 (userType 확인 전)
    // ========================================
    if (!isInitialized) {
        return <Loading message="사용자 정보를 확인하는 중..." />
    }

    // ========================================
    // 5. Render - 본사 사용자 협력사 선택 페이지
    // ========================================
    if (userType === 'HEADQUARTERS' && !selectedSupplierId) {
        return <SupplierSelectionPage onSupplierSelect={handleSupplierSelect} />;
    }

    // ========================================
    // 6. Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">
                    협력사 상세
                    {userType === 'HEADQUARTERS' && selectedSupplierName && (
                        <span style={{
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: 'var(--vroong-green)',
                            marginLeft: '12px',
                        }}>
                            {selectedSupplierName}
                        </span>
                    )}
                </h1>
                {/* 본사 사용자용 뒤로가기 버튼 */}
                {userType === 'HEADQUARTERS' && selectedSupplierId && (
                    <button
                        className="search-btn"
                        onClick={handleBackToSelection}
                        style={{ marginLeft: 'auto' }}
                    >
                        <ArrowLeftIcon width={16} height={16} />
                        <span>협력사 목록으로</span>
                    </button>
                )}
            </div>

            {/* 협력사 상세 컨텐츠 */}
            <SupplierDetailPage selectedSupplierId={selectedSupplierId} onUnsavedChanges={setHasUnsavedChanges} />

            {/* 페이지 이동 확인 모달 */}
            <ConfirmModal
                isOpen={showLeaveModal}
                title="저장하지 않은 변경사항"
                message="저장하지 않은 변경사항이 있습니다. 저장하지 않고 이동하시겠습니까?"
                confirmText="이동"
                cancelText="취소"
                onConfirm={handleConfirmLeave}
                onCancel={handleCancelLeave}
            />
        </div>
    );
};

export default SupplierManagement;
